/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { registrationService, uploadService } from "@/lib/services";
import { CheckCircle, CreditCard, Loader2, Upload } from "lucide-react";
import Image from "next/image";
import Script from "next/script";
import { useCallback, useState } from "react";

const CONFIG = {
	// Registration fee in Naira (e.g., 50000 = ₦50,000)
	registrationFee: 50000,

	// Currency settings
	currency: "NGN",
	currencySymbol: "₦",

	// Payment providers - set to true to enable, false to disable
	paymentProviders: {
		paystack: true,
		flutterwave: false,
	},

	organization: {
		name: "RFFA",
		fullName: "Reliance Future Football Academy",
		logo: "/assets/logo.png",
	},

	maxFileSizeMB: 5,
};

// Type declarations for payment providers
declare global {
	interface Window {
		PaystackPop: any;
		FlutterwaveCheckout: any;
	}
}

// Helper function to format currency
const formatCurrency = (amount: number): string => {
	return `${CONFIG.currencySymbol}${amount.toLocaleString()}`;
};

export default function RegistrationForm() {
	const [formData, setFormData] = useState({
		// Section A
		fullName: "",
		dateOfBirth: "",
		gender: "",
		nationality: "",
		passportNumber: "",
		stateOfOrigin: "",
		homeAddress: "",
		passportPhoto: null as File | null,

		// Section B
		currentSchool: "",
		classGrade: "Primary 1",
		lastTermAverage: "",
		bestSubjects: "",
		subjectsNeedingImprovement: "",
		academicResults: null as File | null,

		// Section C
		preferredPosition: "Goal Keeper",
		dominantFoot: "Right",
		previousClub: "",
		yearsOfExperience: "",
		achievements: "",
		whyJoinRFFA: "",

		// Section D
		height: "",
		weight: "",
		medicalConditions: "No",
		medicalConditionsDetails: "",
		allergies: "No",
		allergiesDetails: "",
		emergencyContactName: "",
		relationship: "Father",
		emergencyContactPhone: "",

		// Section E
		parentFullName: "",
		occupation: "",
		phoneNumber: "",
		emailAddress: "",
		homeAddressDifferent: "",

		// Section F
		confirmTrue: false,
		confirmExcellence: false,
		confirmPhotos: false,
		confirmRules: false,
		parentGuardianName: "",
		parentGuardianSignature: null as File | null,
		parentGuardianSignatureDate: "",
	});

	const [loading, setLoading] = useState(false);
	const [uploadingFiles, setUploadingFiles] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState("");
	const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
	const [paymentMethod, setPaymentMethod] = useState<
		"paystack" | "flutterwave" | null
	>(null);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [processingPayment, setProcessingPayment] = useState(false);
	const [paystackLoaded, setPaystackLoaded] = useState(false);
	const [flutterwaveLoaded, setFlutterwaveLoaded] = useState(false);

	const handleFileChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		field: string
	) => {
		const file = e.target.files?.[0];
		if (file) {
			// Validate file size
			const maxSize = CONFIG.maxFileSizeMB * 1024 * 1024;
			if (file.size > maxSize) {
				setError(`File size must be less than ${CONFIG.maxFileSizeMB}MB`);
				return;
			}
			setFormData({ ...formData, [field]: file });
			setError("");

			// If it's a signature file, create preview
			if (field === "parentGuardianSignature") {
				const reader = new FileReader();
				reader.onloadend = () => {
					setSignaturePreview(reader.result as string);
				};
				reader.readAsDataURL(file);
			}
		}
	};

	const validateForm = (): boolean => {
		// Check required fields
		if (!formData.fullName.trim()) {
			setError("Full name is required");
			return false;
		}
		if (!formData.dateOfBirth) {
			setError("Date of birth is required");
			return false;
		}
		if (!formData.gender) {
			setError("Gender is required");
			return false;
		}
		if (!formData.nationality.trim()) {
			setError("Nationality is required");
			return false;
		}
		if (!formData.stateOfOrigin.trim()) {
			setError("State of origin is required");
			return false;
		}
		if (!formData.homeAddress.trim()) {
			setError("Home address is required");
			return false;
		}
		if (!formData.currentSchool.trim()) {
			setError("Current school is required");
			return false;
		}
		if (!formData.emergencyContactName.trim()) {
			setError("Emergency contact name is required");
			return false;
		}
		if (!formData.emergencyContactPhone.trim()) {
			setError("Emergency contact phone is required");
			return false;
		}
		if (!formData.parentFullName.trim()) {
			setError("Parent/Guardian full name is required");
			return false;
		}
		if (!formData.phoneNumber.trim()) {
			setError("Phone number is required");
			return false;
		}
		if (!formData.emailAddress.trim()) {
			setError("Email address is required");
			return false;
		}
		if (
			!formData.confirmTrue ||
			!formData.confirmExcellence ||
			!formData.confirmPhotos ||
			!formData.confirmRules
		) {
			setError("Please confirm all consent checkboxes");
			return false;
		}
		if (!formData.parentGuardianName.trim()) {
			setError("Parent/Guardian name for signature is required");
			return false;
		}
		if (!formData.parentGuardianSignature) {
			setError("Parent/Guardian signature is required");
			return false;
		}
		if (!formData.parentGuardianSignatureDate) {
			setError("Date of signature is required");
			return false;
		}

		return true;
	};

	const handleFormSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		if (!validateForm()) {
			return;
		}

		// Show payment modal
		setShowPaymentModal(true);
	};

	const generateReference = () => {
		const timestamp = Date.now();
		const random = Math.random().toString(36).substring(2, 8);
		return `${CONFIG.organization.name}-${timestamp}-${random}`.toUpperCase();
	};

	const submitRegistration = useCallback(
		async (paymentReference: string, paymentMethodUsed: string) => {
			setLoading(true);
			setError("");

			try {
				// Upload files first if they exist
				let passportPhotoUrl = "";
				let academicResultsUrl = "";
				let parentGuardianSignatureUrl = "";

				if (
					formData.passportPhoto ||
					formData.academicResults ||
					formData.parentGuardianSignature
				) {
					setUploadingFiles(true);
				}

				if (formData.passportPhoto) {
					const response = await uploadService.uploadImage(
						formData.passportPhoto,
						"registrations/passports"
					);
					if (response.success && response.data) {
						passportPhotoUrl = response.data.url;
					}
				}

				if (formData.academicResults) {
					const response = await uploadService.uploadImage(
						formData.academicResults,
						"registrations/academic"
					);
					if (response.success && response.data) {
						academicResultsUrl = response.data.url;
					}
				}

				if (formData.parentGuardianSignature) {
					const response = await uploadService.uploadImage(
						formData.parentGuardianSignature,
						"registrations/signatures"
					);
					if (response.success && response.data) {
						parentGuardianSignatureUrl = response.data.url;
					}
				}

				setUploadingFiles(false);

				// Prepare form data
				const submitData = new FormData();

				// Section A
				submitData.append("fullName", formData.fullName);
				submitData.append("dateOfBirth", formData.dateOfBirth);
				submitData.append("gender", formData.gender);
				submitData.append("nationality", formData.nationality);
				submitData.append("stateOfOrigin", formData.stateOfOrigin);
				submitData.append("homeAddress", formData.homeAddress);
				if (passportPhotoUrl)
					submitData.append("passportPhotoUrl", passportPhotoUrl);

				// Section B
				submitData.append("currentSchool", formData.currentSchool);
				submitData.append("classGrade", formData.classGrade);
				submitData.append("lastTermAverage", formData.lastTermAverage);
				submitData.append("bestSubjects", formData.bestSubjects);
				submitData.append(
					"subjectsNeedingImprovement",
					formData.subjectsNeedingImprovement
				);
				if (academicResultsUrl)
					submitData.append("academicResultsUrl", academicResultsUrl);

				// Section C
				submitData.append("preferredPosition", formData.preferredPosition);
				submitData.append("dominantFoot", formData.dominantFoot);
				submitData.append("previousClub", formData.previousClub);
				submitData.append("yearsOfExperience", formData.yearsOfExperience);
				submitData.append("achievements", formData.achievements);
				submitData.append("whyJoinRFFA", formData.whyJoinRFFA);

				// Section D
				submitData.append("height", formData.height);
				submitData.append("weight", formData.weight);
				submitData.append("medicalConditions", formData.medicalConditions);
				submitData.append(
					"medicalConditionsDetails",
					formData.medicalConditionsDetails
				);
				submitData.append("allergies", formData.allergies);
				submitData.append("allergiesDetails", formData.allergiesDetails);
				submitData.append(
					"emergencyContactName",
					formData.emergencyContactName
				);
				submitData.append("relationship", formData.relationship);
				submitData.append(
					"emergencyContactPhone",
					formData.emergencyContactPhone
				);

				// Section E
				submitData.append("parentFullName", formData.parentFullName);
				submitData.append("occupation", formData.occupation);
				submitData.append("passportNumber", formData.passportNumber);
				submitData.append("phoneNumber", formData.phoneNumber);
				submitData.append("emailAddress", formData.emailAddress);
				submitData.append(
					"homeAddressDifferent",
					formData.homeAddressDifferent
				);

				// Section F
				submitData.append("parentGuardianName", formData.parentGuardianName);
				if (parentGuardianSignatureUrl)
					submitData.append(
						"parentGuardianSignatureUrl",
						parentGuardianSignatureUrl
					);
				submitData.append(
					"parentGuardianSignatureDate",
					formData.parentGuardianSignatureDate
				);

				// Payment info
				submitData.append("paymentReference", paymentReference);
				submitData.append("paymentMethod", paymentMethodUsed);
				submitData.append("paymentAmount", CONFIG.registrationFee.toString());

				const response = await registrationService.create(submitData);

				if (response.success) {
					setSuccess(true);
					// Reset form
					setFormData({
						fullName: "",
						dateOfBirth: "",
						gender: "",
						nationality: "",
						stateOfOrigin: "",
						passportNumber: "",
						homeAddress: "",
						passportPhoto: null,
						currentSchool: "",
						classGrade: "Primary 1",
						lastTermAverage: "",
						bestSubjects: "",
						subjectsNeedingImprovement: "",
						academicResults: null,
						preferredPosition: "Goal Keeper",
						dominantFoot: "Right",
						previousClub: "",
						yearsOfExperience: "",
						achievements: "",
						whyJoinRFFA: "",
						height: "",
						weight: "",
						medicalConditions: "No",
						medicalConditionsDetails: "",
						allergies: "No",
						allergiesDetails: "",
						emergencyContactName: "",
						relationship: "Father",
						emergencyContactPhone: "",
						parentFullName: "",
						occupation: "",
						phoneNumber: "",
						emailAddress: "",
						homeAddressDifferent: "",
						confirmTrue: false,
						confirmExcellence: false,
						confirmPhotos: false,
						confirmRules: false,
						parentGuardianName: "",
						parentGuardianSignature: null,
						parentGuardianSignatureDate: "",
					});
					setSignaturePreview(null);
					setShowPaymentModal(false);

					// Scroll to top
					window.scrollTo({ top: 0, behavior: "smooth" });
				} else {
					setError(
						response.error || "Failed to submit registration. Please try again."
					);
				}
			} catch (err: any) {
				console.error("Registration error:", err);
				setError(
					err.response?.data?.error ||
						"Failed to submit registration. Please check your internet connection and try again."
				);
			} finally {
				setLoading(false);
				setUploadingFiles(false);
			}
		},
		[formData]
	);

	// Paystack V2 Integration
	const handlePaystackPayment = useCallback(() => {
		if (!window.PaystackPop) {
			setError(
				"Paystack is not loaded. Please refresh the page and try again."
			);
			return;
		}

		setProcessingPayment(true);
		const reference = generateReference();

		try {
			// Using Paystack V2 API with PaystackPop instance
			const paystackInstance = new window.PaystackPop();

			paystackInstance.newTransaction({
				key: process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY,
				email: formData.emailAddress,
				amount: CONFIG.registrationFee * 100, // Paystack expects amount in kobo
				currency: CONFIG.currency,
				ref: reference,
				metadata: {
					custom_fields: [
						{
							display_name: "Player Name",
							variable_name: "player_name",
							value: formData.fullName,
						},
						{
							display_name: "Parent Name",
							variable_name: "parent_name",
							value: formData.parentFullName,
						},
						{
							display_name: "Registration Type",
							variable_name: "registration_type",
							value: `${CONFIG.organization.name} Player Registration`,
						},
					],
				},
				onSuccess: (transaction: any) => {
					console.log("Paystack payment successful:", transaction);
					setProcessingPayment(false);
					submitRegistration(transaction.reference, "paystack");
				},
				onCancel: () => {
					setProcessingPayment(false);
					setError("Payment was cancelled. Please try again.");
				},
				onError: (error: any) => {
					console.error("Paystack error:", error);
					setProcessingPayment(false);
					setError("Payment failed. Please try again.");
				},
			});
		} catch (err) {
			console.error("Paystack initialization error:", err);
			setProcessingPayment(false);
			setError("Failed to initialize payment. Please try again.");
		}
	}, [
		formData.emailAddress,
		formData.fullName,
		formData.parentFullName,
		submitRegistration,
	]);

	const handleFlutterwavePayment = useCallback(() => {
		if (!window.FlutterwaveCheckout) {
			setError(
				"Flutterwave is not loaded. Please refresh the page and try again."
			);
			return;
		}

		setProcessingPayment(true);
		const reference = generateReference();

		window.FlutterwaveCheckout({
			public_key: process.env.NEXT_PUBLIC_FLUTTERWAVE_PUBLIC_KEY,
			tx_ref: reference,
			amount: CONFIG.registrationFee,
			currency: CONFIG.currency,
			payment_options: "card, banktransfer, ussd",
			customer: {
				email: formData.emailAddress,
				phone_number: formData.phoneNumber,
				name: formData.parentFullName,
			},
			customizations: {
				title: `${CONFIG.organization.name} Registration`,
				description: `Registration fee for ${CONFIG.organization.fullName}`,
				logo: CONFIG.organization.logo,
			},
			callback: (response: any) => {
				console.log("Flutterwave payment response:", response);
				setProcessingPayment(false);
				if (
					response.status === "successful" ||
					response.status === "completed"
				) {
					submitRegistration(
						response.transaction_id || response.tx_ref,
						"flutterwave"
					);
				} else {
					setError("Payment was not successful. Please try again.");
				}
			},
			onclose: () => {
				setProcessingPayment(false);
			},
		});
	}, [
		formData.emailAddress,
		formData.phoneNumber,
		formData.parentFullName,
		submitRegistration,
	]);

	const handlePayment = () => {
		if (!paymentMethod) {
			setError("Please select a payment method");
			return;
		}

		setError("");

		if (paymentMethod === "paystack") {
			handlePaystackPayment();
		} else if (paymentMethod === "flutterwave") {
			handleFlutterwavePayment();
		}
	};

	// Get enabled payment providers
	const enabledProviders = Object.entries(CONFIG.paymentProviders)
		.filter(([, enabled]) => enabled)
		.map(([provider]) => provider);

	const isDisabled = loading || uploadingFiles || processingPayment;

	return (
		<>
			{/* Payment Scripts - Only load enabled providers */}
			{CONFIG.paymentProviders.paystack && (
				<Script
					src="https://js.paystack.co/v2/inline.js"
					onLoad={() => setPaystackLoaded(true)}
					onError={() => console.error("Failed to load Paystack script")}
				/>
			)}
			{CONFIG.paymentProviders.flutterwave && (
				<Script
					src="https://checkout.flutterwave.com/v3.js"
					onLoad={() => setFlutterwaveLoaded(true)}
					onError={() => console.error("Failed to load Flutterwave script")}
				/>
			)}

			<section className="py-16 md:py-24 bg-gray-50">
				<div className="container mx-auto px-4 max-w-4xl">
					<h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
						PLAYER REGISTRATION FORM
					</h1>

					{/* Success Message */}
					{success && (
						<div className="mb-6 p-4 bg-green-100 border border-green-400 rounded-lg flex items-start gap-3">
							<CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
							<div>
								<h3 className="font-bold text-green-800">
									Registration Submitted Successfully!
								</h3>
								<p className="text-green-700 text-sm">
									Thank you for your application and payment. We will review it
									and get back to you shortly.
								</p>
							</div>
						</div>
					)}

					{/* Error Message */}
					{error && !showPaymentModal && (
						<div className="mb-6 p-4 bg-red-100 border border-red-400 rounded-lg">
							<p className="text-red-800 font-semibold">{error}</p>
						</div>
					)}

					{uploadingFiles && (
						<div className="mb-6 p-4 bg-blue-100 border border-blue-400 rounded-lg flex items-center gap-3">
							<Loader2 className="w-5 h-5 animate-spin text-blue-600" />
							<p className="text-blue-800 font-semibold">Uploading files...</p>
						</div>
					)}

					<form onSubmit={handleFormSubmit} className="space-y-12">
						{/* SECTION A - PERSONAL INFORMATION */}
						<div>
							<h2 className="text-xl md:text-2xl font-bold text-primary mb-6 uppercase">
								SECTION A — PERSONAL INFORMATION
							</h2>

							<div className="space-y-5">
								<input
									type="text"
									placeholder="Full Name"
									value={formData.fullName}
									onChange={(e) =>
										setFormData({ ...formData, fullName: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="date"
									placeholder="Date of Birth"
									value={formData.dateOfBirth}
									onChange={(e) =>
										setFormData({ ...formData, dateOfBirth: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
									<div className="relative w-full">
										<select
											title="Gender"
											value={formData.gender}
											onChange={(e) =>
												setFormData({ ...formData, gender: e.target.value })
											}
											required
											disabled={isDisabled}
											className="appearance-none w-full px-6 py-4 bg-white border border-gray-300 rounded-lg pr-12 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
										>
											<option value="">Select Gender</option>
											<option>Male</option>
											<option>Female</option>
										</select>

										{/* Custom arrow */}
										<div className="pointer-events-none absolute inset-y-0 right-5 flex items-center">
											<svg
												className="w-4 h-4 text-gray-400"
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path
													fillRule="evenodd"
													d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
									</div>

									<input
										type="text"
										placeholder="Nationality"
										value={formData.nationality}
										onChange={(e) =>
											setFormData({ ...formData, nationality: e.target.value })
										}
										required
										disabled={isDisabled}
										className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
									/>
								</div>

								<input
									type="text"
									placeholder="Passport Number (Optional)"
									value={formData.passportNumber}
									onChange={(e) =>
										setFormData({ ...formData, passportNumber: e.target.value })
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="text"
									placeholder="State of Origin"
									value={formData.stateOfOrigin}
									onChange={(e) =>
										setFormData({ ...formData, stateOfOrigin: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="text"
									placeholder="Home Address"
									value={formData.homeAddress}
									onChange={(e) =>
										setFormData({ ...formData, homeAddress: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<div>
									<label className="block text-gray-700 mb-3 font-semibold">
										Passport Photo
									</label>
									<label className="flex items-center justify-center w-full px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors bg-white">
										<div className="flex flex-col items-center">
											<Upload className="w-8 h-8 text-gray-400 mb-2" />
											<span className="text-gray-600">
												{formData.passportPhoto
													? formData.passportPhoto.name
													: "Click to upload or drag and drop"}
											</span>
											<span className="text-gray-500 text-sm">
												PNG, JPG, GIF up to {CONFIG.maxFileSizeMB}MB
											</span>
										</div>
										<input
											type="file"
											accept="image/*"
											onChange={(e) => handleFileChange(e, "passportPhoto")}
											disabled={isDisabled}
											className="hidden"
										/>
									</label>
								</div>
							</div>
						</div>

						{/* SECTION B - ACADEMIC INFORMATION */}
						<div>
							<h2 className="text-xl md:text-2xl font-bold text-primary mb-6 uppercase">
								SECTION B — ACADEMIC INFORMATION
							</h2>

							<div className="space-y-5">
								<input
									type="text"
									placeholder="Current School"
									value={formData.currentSchool}
									onChange={(e) =>
										setFormData({ ...formData, currentSchool: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<div className="relative w-full">
									<select
										title="Class/Grade"
										value={formData.classGrade}
										onChange={(e) =>
											setFormData({ ...formData, classGrade: e.target.value })
										}
										required
										disabled={isDisabled}
										className="w-full appearance-none px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
									>
										<option>Primary 1</option>
										<option>Primary 2</option>
										<option>Primary 3</option>
										<option>Primary 4</option>
										<option>Primary 5</option>
										<option>Primary 6</option>
										<option>JSS 1</option>
										<option>JSS 2</option>
										<option>JSS 3</option>
										<option>SSS 1</option>
										<option>SSS 2</option>
										<option>SSS 3</option>
									</select>

									{/* Custom arrow */}
									<div className="pointer-events-none absolute inset-y-0 right-5 flex items-center">
										<svg
											className="w-4 h-4 text-gray-400"
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path
												fillRule="evenodd"
												d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
								</div>

								<input
									type="number"
									placeholder="Last Term Average (%)"
									value={formData.lastTermAverage}
									onChange={(e) =>
										setFormData({
											...formData,
											lastTermAverage: e.target.value,
										})
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<textarea
									placeholder="Best Subjects"
									value={formData.bestSubjects}
									onChange={(e) =>
										setFormData({ ...formData, bestSubjects: e.target.value })
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 min-h-24 resize-none"
								/>

								<textarea
									placeholder="Subjects Needing Improvement"
									value={formData.subjectsNeedingImprovement}
									onChange={(e) =>
										setFormData({
											...formData,
											subjectsNeedingImprovement: e.target.value,
										})
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 min-h-24 resize-none"
								/>

								<div>
									<label className="block text-gray-700 mb-3 font-semibold">
										Academic Results
									</label>
									<label className="flex items-center justify-center w-full px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors bg-white">
										<div className="flex flex-col items-center">
											<Upload className="w-8 h-8 text-gray-400 mb-2" />
											<span className="text-gray-600">
												{formData.academicResults
													? formData.academicResults.name
													: "Click to upload or drag and drop"}
											</span>
											<span className="text-gray-500 text-sm">
												PNG, JPG, PDF up to {CONFIG.maxFileSizeMB}MB
											</span>
										</div>
										<input
											type="file"
											accept="image/*,.pdf"
											onChange={(e) => handleFileChange(e, "academicResults")}
											disabled={isDisabled}
											className="hidden"
										/>
									</label>
								</div>
							</div>
						</div>

						{/* SECTION C - FOOTBALL/SPORTS INFORMATION */}
						<div>
							<h2 className="text-xl md:text-2xl font-bold text-primary mb-6 uppercase">
								SECTION C — FOOTBALL/SPORTS INFORMATION
							</h2>

							<div className="space-y-5">
								<div className="relative w-full">
									<select
										title="Preferred Position"
										value={formData.preferredPosition}
										onChange={(e) =>
											setFormData({
												...formData,
												preferredPosition: e.target.value,
											})
										}
										required
										disabled={isDisabled}
										className="w-full appearance-none px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
									>
										<option>Goal Keeper</option>
										<option>Defender</option>
										<option>Midfielder</option>
										<option>Forward</option>
										<option>Striker</option>
									</select>

									{/* Custom arrow */}
									<div className="pointer-events-none absolute inset-y-0 right-5 flex items-center">
										<svg
											className="w-4 h-4 text-gray-400"
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path
												fillRule="evenodd"
												d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
								</div>

								<div className="relative w-full">
									<select
										title="Dominant Foot"
										value={formData.dominantFoot}
										onChange={(e) =>
											setFormData({ ...formData, dominantFoot: e.target.value })
										}
										required
										disabled={isDisabled}
										className="w-full appearance-none px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
									>
										<option>Right</option>
										<option>Left</option>
										<option>Both</option>
									</select>

									{/* Custom arrow */}
									<div className="pointer-events-none absolute inset-y-0 right-5 flex items-center">
										<svg
											className="w-4 h-4 text-gray-400"
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path
												fillRule="evenodd"
												d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
								</div>

								<input
									type="text"
									placeholder="Previous Club (if any)"
									value={formData.previousClub}
									onChange={(e) =>
										setFormData({ ...formData, previousClub: e.target.value })
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="number"
									placeholder="Years of Experience"
									value={formData.yearsOfExperience}
									onChange={(e) =>
										setFormData({
											...formData,
											yearsOfExperience: e.target.value,
										})
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<textarea
									placeholder="Achievements & Awards"
									value={formData.achievements}
									onChange={(e) =>
										setFormData({ ...formData, achievements: e.target.value })
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 min-h-24 resize-none"
								/>

								<textarea
									placeholder="Why Do You Want To Join RFFA?"
									value={formData.whyJoinRFFA}
									onChange={(e) =>
										setFormData({ ...formData, whyJoinRFFA: e.target.value })
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 min-h-24 resize-none"
								/>
							</div>
						</div>

						{/* SECTION D - HEALTH & EMERGENCY */}
						<div>
							<h2 className="text-xl md:text-2xl font-bold text-primary mb-6 uppercase">
								SECTION D — HEALTH & EMERGENCY INFORMATION
							</h2>

							<div className="space-y-5">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
									<input
										type="number"
										placeholder="Height (cm)"
										value={formData.height}
										onChange={(e) =>
											setFormData({ ...formData, height: e.target.value })
										}
										disabled={isDisabled}
										className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
									/>

									<input
										type="number"
										placeholder="Weight (kg)"
										value={formData.weight}
										onChange={(e) =>
											setFormData({ ...formData, weight: e.target.value })
										}
										disabled={isDisabled}
										className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
									/>
								</div>

								<div>
									<label className="block text-gray-700 mb-3 font-semibold">
										Do You Have Any Medical Conditions?
									</label>

									<div className="relative w-full flex">
										<select
											title="Medical Conditions"
											value={formData.medicalConditions}
											onChange={(e) =>
												setFormData({
													...formData,
													medicalConditions: e.target.value,
												})
											}
											required
											disabled={isDisabled}
											className="w-full flex-1 appearance-none md:w-32 px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
										>
											<option>No</option>
											<option>Yes</option>
										</select>

										{/* Custom arrow */}
										<div className="pointer-events-none absolute inset-y-0 right-5 flex items-center">
											<svg
												className="w-4 h-4 text-gray-400"
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path
													fillRule="evenodd"
													d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
									</div>
								</div>

								{formData.medicalConditions === "Yes" && (
									<textarea
										placeholder="Please describe the medical conditions..."
										value={formData.medicalConditionsDetails}
										onChange={(e) =>
											setFormData({
												...formData,
												medicalConditionsDetails: e.target.value,
											})
										}
										disabled={isDisabled}
										className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 min-h-24 resize-none"
									/>
								)}

								<div>
									<label className="block text-gray-700 mb-3 font-semibold">
										Do You Have Any Allergies?
									</label>
									<div className="relative w-full flex">
										<select
											title="Allergies"
											value={formData.allergies}
											onChange={(e) =>
												setFormData({ ...formData, allergies: e.target.value })
											}
											required
											disabled={isDisabled}
											className="w-full flex-1 appearance-none md:w-32 px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
										>
											<option>No</option>
											<option>Yes</option>
										</select>

										{/* Custom arrow */}
										<div className="pointer-events-none absolute inset-y-0 right-5 flex items-center">
											<svg
												className="w-4 h-4 text-gray-400"
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path
													fillRule="evenodd"
													d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
									</div>
								</div>

								{formData.allergies === "Yes" && (
									<textarea
										placeholder="Please describe the allergies..."
										value={formData.allergiesDetails}
										onChange={(e) =>
											setFormData({
												...formData,
												allergiesDetails: e.target.value,
											})
										}
										disabled={isDisabled}
										className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 min-h-24 resize-none"
									/>
								)}

								<input
									type="text"
									placeholder="Emergency Contact Name"
									value={formData.emergencyContactName}
									onChange={(e) =>
										setFormData({
											...formData,
											emergencyContactName: e.target.value,
										})
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<div>
									<label className="block text-gray-700 mb-2">
										Relationship
									</label>

									<div className="relative w-full flex">
										<select
											title="Relationship"
											value={formData.relationship}
											onChange={(e) =>
												setFormData({
													...formData,
													relationship: e.target.value,
												})
											}
											disabled={isDisabled}
											className="w-full flex-1 appearance-none md:w-64 px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
										>
											<option>Father</option>
											<option>Mother</option>
											<option>Guardian</option>
											<option>Other</option>
										</select>
										{/* Custom arrow */}
										<div className="pointer-events-none absolute inset-y-0 right-5 flex items-center">
											<svg
												className="w-4 h-4 text-gray-400"
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path
													fillRule="evenodd"
													d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
									</div>
								</div>

								<input
									type="tel"
									placeholder="Emergency Contact Phone"
									value={formData.emergencyContactPhone}
									onChange={(e) =>
										setFormData({
											...formData,
											emergencyContactPhone: e.target.value,
										})
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>
							</div>
						</div>

						{/* SECTION E - PARENT/GUARDIAN INFORMATION */}
						<div>
							<h2 className="text-xl md:text-2xl font-bold text-primary mb-6 uppercase">
								SECTION E — PARENT/GUARDIAN INFORMATION
							</h2>

							<div className="space-y-5">
								<input
									type="text"
									placeholder="Full Name"
									value={formData.parentFullName}
									onChange={(e) =>
										setFormData({ ...formData, parentFullName: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="text"
									placeholder="Occupation"
									value={formData.occupation}
									onChange={(e) =>
										setFormData({ ...formData, occupation: e.target.value })
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="tel"
									placeholder="Phone Number"
									value={formData.phoneNumber}
									onChange={(e) =>
										setFormData({ ...formData, phoneNumber: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="email"
									placeholder="Email Address"
									value={formData.emailAddress}
									onChange={(e) =>
										setFormData({ ...formData, emailAddress: e.target.value })
									}
									required
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>

								<input
									type="text"
									placeholder="Home Address (If Different)"
									value={formData.homeAddressDifferent}
									onChange={(e) =>
										setFormData({
											...formData,
											homeAddressDifferent: e.target.value,
										})
									}
									disabled={isDisabled}
									className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
								/>
							</div>
						</div>

						{/* SECTION F - PARENT/GUARDIAN SIGNATURE */}
						<div>
							<h2 className="text-xl md:text-2xl font-bold text-primary mb-6 uppercase">
								SECTION F — CONSENT & PARENT/GUARDIAN SIGNATURE
							</h2>

							<div className="space-y-6">
								{/* Consent Checkboxes */}
								<div className="space-y-4 pb-6 border-b-2 border-gray-300">
									<label className="flex items-start gap-3">
										<input
											type="checkbox"
											checked={formData.confirmTrue}
											onChange={(e) =>
												setFormData({
													...formData,
													confirmTrue: e.target.checked,
												})
											}
											required
											disabled={isDisabled}
											className="w-5 h-5 mt-1"
										/>
										<span className="text-gray-700">
											I Confirm That All Information Provided Is True And
											Accurate.
										</span>
									</label>

									<label className="flex items-start gap-3">
										<input
											type="checkbox"
											checked={formData.confirmExcellence}
											onChange={(e) =>
												setFormData({
													...formData,
													confirmExcellence: e.target.checked,
												})
											}
											required
											disabled={isDisabled}
											className="w-5 h-5 mt-1"
										/>
										<span className="text-gray-700">
											I Understand That Academic Excellence And Discipline Are
											Required At {CONFIG.organization.name}.
										</span>
									</label>

									<label className="flex items-start gap-3">
										<input
											type="checkbox"
											checked={formData.confirmPhotos}
											onChange={(e) =>
												setFormData({
													...formData,
													confirmPhotos: e.target.checked,
												})
											}
											required
											disabled={isDisabled}
											className="w-5 h-5 mt-1"
										/>
										<span className="text-gray-700">
											I Permit My Child&apos;s Photos/Videos To Be Used For
											Academy Purposes.
										</span>
									</label>

									<label className="flex items-start gap-3">
										<input
											type="checkbox"
											checked={formData.confirmRules}
											onChange={(e) =>
												setFormData({
													...formData,
													confirmRules: e.target.checked,
												})
											}
											required
											disabled={isDisabled}
											className="w-5 h-5 mt-1"
										/>
										<span className="text-gray-700">
											I Agree To Abide By {CONFIG.organization.name}&apos;s
											Rules And Policies.
										</span>
									</label>
								</div>

								{/* Parent/Guardian Signature Section */}
								<div className="space-y-5">
									<div>
										<label className="block text-gray-700 font-semibold mb-3">
											Parent/Guardian Full Name *
										</label>
										<input
											type="text"
											placeholder="Enter parent/guardian full name"
											value={formData.parentGuardianName}
											onChange={(e) =>
												setFormData({
													...formData,
													parentGuardianName: e.target.value,
												})
											}
											required
											disabled={isDisabled}
											className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
										/>
									</div>

									<div>
										<label className="block text-gray-700 font-semibold mb-3">
											Signature (Image) *
										</label>
										<label className="flex items-center justify-center w-full px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors bg-white">
											<div className="flex flex-col items-center">
												<Upload className="w-8 h-8 text-gray-400 mb-2" />
												<span className="text-gray-600">
													{formData.parentGuardianSignature
														? formData.parentGuardianSignature.name
														: "Click to upload signature or drag and drop"}
												</span>
												<span className="text-gray-500 text-sm">
													PNG, JPG, GIF up to {CONFIG.maxFileSizeMB}MB
												</span>
											</div>
											<input
												type="file"
												accept="image/*"
												onChange={(e) =>
													handleFileChange(e, "parentGuardianSignature")
												}
												disabled={isDisabled}
												className="hidden"
											/>
										</label>
										{signaturePreview && (
											<div className="mt-4 p-4 border border-gray-300 rounded-lg bg-gray-50">
												<p className="text-sm text-gray-600 mb-3 font-semibold">
													Signature Preview:
												</p>
												<Image
													src={signaturePreview}
													alt="Signature preview"
													width={400}
													height={160}
													className="max-h-40 border border-gray-200 rounded object-contain"
												/>
											</div>
										)}
									</div>

									<div>
										<label className="block text-gray-700 font-semibold mb-3">
											Date of Signature *
										</label>
										<input
											title="Date of signature"
											type="date"
											value={formData.parentGuardianSignatureDate}
											onChange={(e) =>
												setFormData({
													...formData,
													parentGuardianSignatureDate: e.target.value,
												})
											}
											required
											disabled={isDisabled}
											className="w-full px-6 py-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
											max={new Date().toISOString().split("T")[0]}
										/>
									</div>
								</div>
							</div>
						</div>

						{/* Registration Fee Notice - Moved to bottom before submit */}
						<div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
							<div className="flex items-start gap-4">
								<CreditCard className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
								<div>
									<h3 className="font-bold text-gray-900 text-lg mb-2">
										Registration Fee: {formatCurrency(CONFIG.registrationFee)}
									</h3>
									<p className="text-gray-700 text-sm">
										A{" "}
										<span className="font-semibold text-amber-700">
											non-refundable
										</span>{" "}
										registration fee of {formatCurrency(CONFIG.registrationFee)}{" "}
										is required to complete your application. Payment can be
										made via{" "}
										{enabledProviders
											.map((p) => p.charAt(0).toUpperCase() + p.slice(1))
											.join(" or ")}{" "}
										after filling out the form.
									</p>
								</div>
							</div>
						</div>

						<button
							type="submit"
							disabled={isDisabled}
							className="mt-8 px-12 py-4 bg-primary hover:bg-blue-700 text-white font-bold rounded-full transition-colors shadow-lg hover:shadow-xl text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
						>
							{loading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin" />
									Submitting...
								</>
							) : (
								<>
									<CreditCard className="w-5 h-5" />
									Proceed to Payment ({formatCurrency(CONFIG.registrationFee)})
								</>
							)}
						</button>
					</form>
				</div>
			</section>

			{/* Payment Modal */}
			{showPaymentModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8">
						<h2 className="text-2xl font-bold text-gray-900 mb-2">
							Complete Payment
						</h2>
						<p className="text-gray-600 mb-6">
							Select your preferred payment method to complete your
							registration.
						</p>

						{/* Payment Summary */}
						<div className="bg-gray-50 rounded-lg p-4 mb-6">
							<div className="flex justify-between items-center mb-2">
								<span className="text-gray-600">Registration Fee</span>
								<span className="font-semibold">
									{formatCurrency(CONFIG.registrationFee)}
								</span>
							</div>
							<div className="flex justify-between items-center text-lg font-bold border-t pt-2">
								<span>Total</span>
								<span className="text-primary">
									{formatCurrency(CONFIG.registrationFee)}
								</span>
							</div>
						</div>

						{/* Error in modal */}
						{error && (
							<div className="mb-4 p-3 bg-red-100 border border-red-400 rounded-lg">
								<p className="text-red-800 text-sm">{error}</p>
							</div>
						)}

						{/* Payment Options */}
						<div className="space-y-3 mb-6">
							{/* Paystack Option */}
							{CONFIG.paymentProviders.paystack && (
								<button
									type="button"
									onClick={() => setPaymentMethod("paystack")}
									disabled={!paystackLoaded || processingPayment}
									className={`w-full p-4 border-2 rounded-lg flex items-center gap-4 transition-all ${
										paymentMethod === "paystack"
											? "border-primary bg-primary/5"
											: "border-gray-200 hover:border-gray-300"
									} ${!paystackLoaded ? "opacity-50 cursor-not-allowed" : ""}`}
								>
									<div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
										<span className="text-white font-bold text-sm">PS</span>
									</div>
									<div className="text-left flex-1">
										<p className="font-semibold text-gray-900">Paystack</p>
										<p className="text-sm text-gray-500">
											Card, Bank Transfer, USSD
										</p>
									</div>
									{paymentMethod === "paystack" && (
										<CheckCircle className="w-6 h-6 text-primary" />
									)}
								</button>
							)}

							{/* Flutterwave Option */}
							{CONFIG.paymentProviders.flutterwave && (
								<button
									type="button"
									onClick={() => setPaymentMethod("flutterwave")}
									disabled={!flutterwaveLoaded || processingPayment}
									className={`w-full p-4 border-2 rounded-lg flex items-center gap-4 transition-all ${
										paymentMethod === "flutterwave"
											? "border-primary bg-primary/5"
											: "border-gray-200 hover:border-gray-300"
									} ${
										!flutterwaveLoaded ? "opacity-50 cursor-not-allowed" : ""
									}`}
								>
									<div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
										<span className="text-white font-bold text-sm">FW</span>
									</div>
									<div className="text-left flex-1">
										<p className="font-semibold text-gray-900">Flutterwave</p>
										<p className="text-sm text-gray-500">
											Card, Bank Transfer, USSD, Mobile Money
										</p>
									</div>
									{paymentMethod === "flutterwave" && (
										<CheckCircle className="w-6 h-6 text-primary" />
									)}
								</button>
							)}
						</div>

						{/* Non-refundable disclaimer in modal */}
						<p className="text-xs text-gray-500 mb-4 text-center">
							By proceeding, you agree that this registration fee is{" "}
							<span className="font-semibold">non-refundable</span>.
						</p>

						{/* Action Buttons */}
						<div className="flex gap-3">
							<button
								type="button"
								onClick={() => {
									setShowPaymentModal(false);
									setPaymentMethod(null);
									setError("");
								}}
								disabled={processingPayment}
								className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handlePayment}
								disabled={!paymentMethod || processingPayment}
								className="flex-1 px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
							>
								{processingPayment ? (
									<>
										<Loader2 className="w-5 h-5 animate-spin" />
										Processing...
									</>
								) : (
									"Pay Now"
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
